import React, { useState, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { Flex, Text, Button, toast } from "@jsq/ds/core";
import { Colors, Sizes } from "@jsq/ds/styles";
import { DataStudioJobSubstateEnum, DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import {
  useCrmGeneratePresignedUrlsMutation,
  useCreateOnboardingJobStepMutation,
} from "~/graphql/supergraphHooks";
import { WorkflowStep, DataStudioEnvironment } from "../../constants";
import { useCrmImportJobValidation } from "../../hooks/useCrmImportQueries";
import { useIdQueryParam } from "../../hooks/useIdQueryParam";
import { getWorkflowRoute } from "../../utils/workflowUtils";
import LoadingOverlay from "../LoadingOverlay";
import * as styles from "../shared/styles_vanilla.css";
import DataValidationPlaceholder from "./DataValidationPlaceholder";
import DataValidationStats from "./DataValidationStats";
import FileValidationCard from "./FileValidationCard";
import ReplaceFileModal from "./ReplaceFileModal";
import * as localStyles from "./styles_vanilla.css";
import {
  downloadFileFromS3,
  handleDownloadErrors as handleDownloadErrorsUtil,
  handleDownloadMappedFile as handleDownloadMappedFileUtil,
  transformValidationDataForDisplay,
  createDownloadChecker,
  determineValidationDisplayState,
} from "./utils";

const DataValidation: React.FC = () => {
  const history = useHistory();
  const { idFromUrl, getQueryParamsWithId } = useIdQueryParam();
  const [replaceFileModalOpen, setReplaceFileModalOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [selectedToolType, setSelectedToolType] = useState<string>("");
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSubmittingNext, setIsSubmittingNext] = useState(false);

  const {
    data: validationData,
    error: validationError,
    loading: validationLoading,
    refetch: refetchValidation,
  } = useCrmImportJobValidation(idFromUrl || "", {
    skip: !idFromUrl,
  });

  useEffect(() => {
    if (!validationLoading && !validationError && !validationData && idFromUrl) {
      toast.error(`The requested job ID "${idFromUrl}" is invalid or does not exist.`);
    }
  }, [validationLoading, validationError, validationData, idFromUrl]);

  const [generatePresignedUrlsMutation] = useCrmGeneratePresignedUrlsMutation();

  const [createJobStep] = useCreateOnboardingJobStepMutation({
    onCompleted: () => {
      toast.success("Moving to test data release stage!");
      history.push(getWorkflowRoute(WorkflowStep.TEST_DATA_RELEASE, getQueryParamsWithId()));
    },
    onError: (error: ApolloError) => {
      toast.error(`Failed to move to test data release: ${error.message}`);
    },
  });

  // Handle refresh functionality
  const handleRefresh = async () => {
    if (!refetchValidation) return;

    setIsRefreshing(true);
    try {
      await refetchValidation();
    } catch {
      toast.error("Failed to refresh validation data. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const shouldShowValidationResults = validationData?.substate === "DATA_VALIDATION";

  const handleNext = async () => {
    if (!validationData || isSubmittingNext) {
      if (!validationData) {
        toast.error("Invalid job data. Cannot proceed to test data release.");
      }
      return;
    }

    setIsSubmittingNext(true);

    try {
      await createJobStep({
        variables: {
          input: {
            jobType: DataStudioJobTypeEnum.CrmImport,
            arenaDomain: validationData.arenaDomain,
            arenaUserEmail: validationData.arenaUserEmail,
            env: DataStudioEnvironment.TEST,
            jobId: validationData.jobId,
            substate: DataStudioJobSubstateEnum.TestDataRelease,
            createdAt: validationData.createdAt,
          },
        },
      });
    } catch {
      // Error handling is done in onError callback
    } finally {
      setIsSubmittingNext(false);
    }
  };

  // Create a bound version of downloadFileFromS3 with the current context
  const downloadFileFromS3Bound = (
    s3Key: string,
    fileName: string,
    fileType: "errors" | "mapped",
  ) => {
    if (!validationData) return Promise.resolve();
    return downloadFileFromS3(
      s3Key,
      fileName,
      fileType,
      validationData,
      generatePresignedUrlsMutation,
      downloadingFiles,
      setDownloadingFiles,
    );
  };

  const handleDownloadErrors = (fileName: string) => {
    if (!validationData) return;
    handleDownloadErrorsUtil(fileName, validationData, downloadFileFromS3Bound);
  };

  const handleDownloadMappedFile = (fileName: string) => {
    if (!validationData) return;
    handleDownloadMappedFileUtil(fileName, validationData, downloadFileFromS3Bound);
  };

  const handleReplaceFile = (fileName: string, toolType: string) => {
    setSelectedFileName(fileName);
    setSelectedToolType(toolType);
    setReplaceFileModalOpen(true);
  };

  // Determine display state using utility function
  const displayState = determineValidationDisplayState(
    validationError,
    shouldShowValidationResults,
    validationLoading,
    validationData,
  );

  // Show error overlay for network/technical errors
  if (displayState.shouldShowErrorOverlay) {
    return (
      <LoadingOverlay
        error={validationError}
        onRetry={() => window.location.reload()}
        onCancel={() => history.goBack()}
      >
        <div />
      </LoadingOverlay>
    );
  }

  // Show placeholder for loading states or business logic errors
  if (displayState.shouldShowPlaceholder) {
    const isBusinessLogicError = validationError?.name === "BusinessLogicError";
    const isJobFailed = validationData?.jobStatus === "FAILED";

    return (
      <DataValidationPlaceholder
        message={displayState.placeholderMessage}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        showLoader={displayState.showLoader}
        isError={isBusinessLogicError || isJobFailed}
      />
    );
  }

  // Transform real validation data from API for display
  const displayValidationStats = validationData?.validationStats;
  const displayFilesData = transformValidationDataForDisplay(validationData?.toolValidations);

  // Determine if validation is complete and has no critical errors
  const isValidationComplete = validationData?.isValidationComplete || false;
  const hasValidationErrors = validationData?.hasValidationErrors || false;

  // Show the actual validation results
  return (
    <Flex flexDirection="column" gap={Sizes.LG} className={localStyles.mainContainer}>
      <Flex flexDirection="column" gap={Sizes.LG} className={styles.stageContainer}>
        <Text variant="h4" color={Colors.Neutral900}>
          Data Validation
        </Text>

        <DataValidationStats stats={displayValidationStats} />
      </Flex>

      <FileValidationCard
        filesData={displayFilesData}
        onDownloadErrors={handleDownloadErrors}
        onDownloadMappedFile={handleDownloadMappedFile}
        onReplaceFile={handleReplaceFile}
        isDownloadingErrors={createDownloadChecker(downloadingFiles).isDownloadingErrors}
        isDownloadingMappedFile={
          createDownloadChecker(downloadingFiles).isDownloadingMappedFile
        }
      />
      <Flex justifyContent="flex-end">
        <Button
          variant="custom"
          backgroundColor={Colors.Neutral900}
          textColor={Colors.Neutral0}
          onClick={handleNext}
          disabled={
            !isValidationComplete || hasValidationErrors || isSubmittingNext || !validationData
          }
          isLoading={isSubmittingNext}
          className={styles.navigationButton}
        >
          Next: Test Data Release
        </Button>
      </Flex>

      <ReplaceFileModal
        showModal={replaceFileModalOpen}
        onClose={() => setReplaceFileModalOpen(false)}
        fileName={selectedFileName}
        toolType={selectedToolType}
        onFileReplaced={() => refetchValidation?.()}
      />
    </Flex>
  );
};

export default DataValidation;
